import React, { useId } from 'react';
import {
  Box,
  Drawer as MuiDrawer,
  DrawerProps as MuiDrawerProps,
  styled,
  Typography,
} from '@mui/material';
import { CloseButton } from 'components/CloseButton/CloseButton';
import { radius } from 'themes/radius';

export type DrawerPlacement = 'top' | 'right' | 'bottom' | 'left';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'full';

type MuiDrawerOmitKeys = 'anchor' | 'open' | 'onClose' | 'children' | 'variant';

export type OmittedMuiDrawerProps = Omit<MuiDrawerProps, MuiDrawerOmitKeys>;

export interface DrawerProps extends OmittedMuiDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  placement?: DrawerPlacement;
  size?: DrawerSize;
  title?: string;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  ariaLabel?: string;
}

// Horizontal placements are sized by width, vertical placements by height.
const SIZE_MAP: Record<DrawerSize, string> = {
  sm: '320px',
  md: '480px',
  lg: '640px',
  full: '100%',
};

const isHorizontal = (placement: DrawerPlacement) => placement === 'left' || placement === 'right';

// Round only the corners on the drawer's leading edge (the edge facing the viewport interior).
const paperRadius = (placement: DrawerPlacement) => {
  switch (placement) {
    case 'right':
      return { borderTopLeftRadius: radius.xs, borderBottomLeftRadius: radius.xs };
    case 'left':
      return { borderTopRightRadius: radius.xs, borderBottomRightRadius: radius.xs };
    case 'top':
      return { borderBottomLeftRadius: radius.xs, borderBottomRightRadius: radius.xs };
    case 'bottom':
    default:
      return { borderTopLeftRadius: radius.xs, borderTopRightRadius: radius.xs };
  }
};

interface StyledDrawerProps {
  placement: DrawerPlacement;
  size: DrawerSize;
}

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'placement' && prop !== 'size',
})<StyledDrawerProps>(({ placement, size }) => {
  const dimension = SIZE_MAP[size];
  return {
    '& .MuiDrawer-paper': {
      display: 'flex',
      flexDirection: 'column',
      ...paperRadius(placement),
      ...(isHorizontal(placement)
        ? { width: dimension, maxWidth: '100vw' }
        : { height: dimension, maxHeight: '100vh' }),
    },
  };
});

const DrawerHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minHeight: 56,
  padding: theme.spacing(1),
}));

const DrawerBody = styled(Box)({
  flex: 1,
  overflowY: 'auto',
});

const DrawerFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
}));

export const Drawer = ({
  open,
  onClose,
  children,
  placement = 'right',
  size = 'md',
  title,
  footer,
  showCloseButton = false,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  ariaLabel,
  PaperProps,
  ...restProps
}: DrawerProps) => {
  const titleId = useId();

  const handleClose: NonNullable<MuiDrawerProps['onClose']> = (_event, reason) => {
    if (reason === 'backdropClick' && !closeOnOverlayClick) return;
    onClose();
  };

  return (
    <StyledDrawer
      // temporary variant renders a modal backdrop that dismisses on click and Escape
      variant="temporary"
      anchor={placement}
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={!closeOnEsc}
      placement={placement}
      size={size}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': title ? titleId : undefined,
        'aria-label': title ? undefined : ariaLabel,
        ...PaperProps,
      }}
      {...restProps}
    >
      {(title || showCloseButton) && (
        <DrawerHeader>
          {title && (
            <Typography
              id={titleId}
              variant="h6"
              component="h2"
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                maxWidth: 'calc(100% - 96px)',
                textAlign: 'center',
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          )}
          <CloseButton onClick={onClose} edge="end" sx={{ mr: 0.5 }} />
        </DrawerHeader>
      )}
      <DrawerBody>{children}</DrawerBody>
      {footer && <DrawerFooter>{footer}</DrawerFooter>}
    </StyledDrawer>
  );
};
